import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, GitCompare } from "lucide-react";
import { ActionMenu, Badge, Button } from "@h2j-data-team/ui";
import type { AppAnalysisDetail } from "@repo/shared";

import { renderLink } from "@/components/router-link";
import { appAnalysisListQueryOptions } from "@/features/app-analysis/api";
import { useFormatDate } from "@/hooks/use-format-date";

interface CompareAnalysisMenuProps {
  analysis: AppAnalysisDetail;
}

/**
 * "Compare" entry point: the other completed analyses of the same app, most
 * recent first. Hidden when this analysis is not completed or there is nothing
 * to compare it against.
 */
export function CompareAnalysisMenu({ analysis }: CompareAnalysisMenuProps) {
  const { t } = useTranslation("analyses");
  const { formatDateTime } = useFormatDate();

  const { data } = useQuery(
    appAnalysisListQueryOptions({
      connectionId: analysis.connectionId,
      appId: analysis.appId,
      status: "completed",
      limit: 20,
    }),
  );

  const candidates = (data?.data ?? []).filter((a) => a.id !== analysis.id);
  if ((analysis.status ?? "completed") !== "completed" || candidates.length === 0) return null;

  // "previous" marks the newest analysis that is actually older than this one —
  // the list may also hold newer runs when an older analysis is open.
  const analyzedAt = Date.parse(analysis.analyzedAt);
  const previousId = candidates.find((a) => Date.parse(a.analyzedAt) < analyzedAt)?.id;

  return (
    <ActionMenu
      label={t("compare.menuLabel")}
      renderLink={renderLink}
      trigger={
        <Button variant="outline" size="sm">
          <GitCompare />
          {t("compare.button")}
          <ChevronDown />
        </Button>
      }
      items={candidates.map((candidate) => ({
        id: candidate.id,
        href: `/analyses/${analysis.id}/compare/${candidate.id}`,
        label: (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono">{formatDateTime(candidate.analyzedAt)}</span>
            {candidate.id === previousId && (
              <Badge variant="secondary">{t("compare.previousBadge")}</Badge>
            )}
          </span>
        ),
        meta: candidate.health && (
          <span className="font-mono tabular-nums">
            {candidate.health.score} {t("health.score")}
          </span>
        ),
      }))}
    />
  );
}
